import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Link, ModalPortal, Text } from "@livechat/design-system-react-components";

import { Icon, IconsEnum } from "components/icon";
import { ConfirmModal } from "components/modals/new/ConfirmModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { openInNewCard } from "helpers/routing";
import { AgentActions, selectLastClosedGoogleVerification } from "store/entities/agents";
import { LicenseActions, selectGoogleForwardingConfig } from "store/entities/license";
import { StackingContextLevel } from "styles/stacking-context-level";

const GMAIL_FORWARDING_SETTINGS_URL = "https://mail.google.com/mail/u/0/#settings/fwdandpop";

interface IProps {
  onClose: () => void;
}
enum VerificationStep {
  Verify = "verify",
  GmailSettings = "gmail-settings",
}

export const VerifyGoogleModal: FC<IProps> = ({ onClose }) => {
  const [step, setStep] = useState<VerificationStep>(VerificationStep.Verify);
  const dispatch = useDispatch();
  const googleForwardingConfig = useSelector(selectGoogleForwardingConfig);
  const lastClosedGoogleVerification = useSelector(selectLastClosedGoogleVerification);
  const isFirstStep = step === VerificationStep.Verify;

  // ensure that even when the config is cleared due to handleConfirm, we're still in an OK state
  const [preservedForwardingConfig] = useState<typeof googleForwardingConfig>(googleForwardingConfig);

  useEffect(() => {
    trackEngagement("Email addresses: Verify link modal opened");
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleVerifyForwardingClick = () => {
    if (!preservedForwardingConfig) {
      return;
    }
    trackEngagement("Email addresses: Verify forwarding button clicked");
    openInNewCard(preservedForwardingConfig.link);
    dispatch(LicenseActions.clearGoogleForwardingLink());
    setStep(VerificationStep.GmailSettings);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleFinishForwardingSetupClick = () => {
    handleGmailForwardingSettings();
    openInNewCard(GMAIL_FORWARDING_SETTINGS_URL);
    handleCloseModal("finished");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCloseModal = (intent: "closed" | "finished") => {
    trackEngagement("Email addresses: Verify link modal closed", EventPlace.Settings, {
      hasClickedVerify: !isFirstStep,
      intent,
    });
    onClose();
    if (preservedForwardingConfig) {
      dispatch(AgentActions.setLastClosedGoogleVerification({ id: preservedForwardingConfig.ticketID }));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleGmailForwardingSettings = () => {
    trackEngagement("Email addresses: Gmail forwarding settings opened externally");
  };

  if (!preservedForwardingConfig || lastClosedGoogleVerification === preservedForwardingConfig.ticketID) {
    return null;
  }

  const content = isFirstStep ? (
    "Click to verify your email forwarding setup in Gmail/GSuite."
  ) : (
    <>
      Lastly, to make forwarding work, you still have to{" "}
      <b>
        select&nbsp;&quot;Forward a copy of incoming mail to&quot;&nbsp;to the added HelpDesk&apos;s forwarding address
      </b>
      , and save changes in{" "}
      <Link href={GMAIL_FORWARDING_SETTINGS_URL} bold={false} target="_blank" onClick={handleGmailForwardingSettings}>
        Gmail&apos;s&nbsp;forwarding&nbsp;settings
      </Link>
      .
    </>
  );

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmModal
        heading={isFirstStep ? "Verify email forwarding in Gmail" : "Finish forwarding setup"}
        icon={<Icon name={IconsEnum.CheckTicketDrawn} width="64px" height="64px" />}
        onCancel={() => handleCloseModal("closed")}
        onConfirm={isFirstStep ? handleVerifyForwardingClick : handleFinishForwardingSetupClick}
        confirmButton={{
          label: isFirstStep ? "Verify forwarding" : "Finish forwarding setup",
          dataTestId: "verify-forwarding-button",
        }}
        hideCancelButton
      >
        <Text css={{ maxWidth: "360px" }}>{content}</Text>
      </ConfirmModal>
    </ModalPortal>
  );
};
